import React from "react";
import { Link } from "react-router-dom";
import dayjs from "@/config/dayjs";
import { withTranslation, type WithTranslation } from "react-i18next";

import { getDocumentLibrary } from "helper/documentModules";
import { isAllowed } from "helper/acl";
import LogApi from "api/LogApi";
import { useNotifications } from "api/queries/useNotifications";
import { withModulesData, type ModulesQueryProps } from "api/queries/useModules";
import type { NotificationItem, UserCustomer, UserData } from "../types/state";
import { withAuthData, type AuthInjectedProps } from "../context/AuthContext";
import roadmapIcon from "../../assets/images/dashboard-roadmap.png";
import tourneesIcon from "../../assets/images/dashboard-tournees.png";
import seminaireInternationalIcon from "../../assets/images/dashboard-seminaire-international.png";
import myformulationIcon from "../../assets/images/dashboard-myformulation.png";

interface DashboardPageOwnProps extends WithTranslation {}

interface DashboardPageStateProps {
  notifications: NotificationItem[];
  notificationsLoaded: boolean;
  userData: UserData;
  customerData: UserCustomer | Record<string, never>;
}

type DashboardPageProps = DashboardPageOwnProps &
  DashboardPageStateProps &
  ModulesQueryProps &
  AuthInjectedProps;

function isUnreadNotification(notification: NotificationItem) {
  return notification.read !== true && notification.read !== 1 && notification.read !== "1";
}

function DashboardPage({
  t,
  i18n,
  notifications,
  notificationsLoaded,
  userData,
  customerData,
  modules,
  adminToken,
}: DashboardPageProps) {
  React.useEffect(() => {
    LogApi.createOne("module_homepage", typeof adminToken === "string" ? adminToken : null).catch(
      () => undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUnreadNotificationCount = () => {
    if (notificationsLoaded) {
      return notifications.filter(isUnreadNotification).length;
    }
    return parseInt(String((customerData as UserCustomer).unread_notification_count || 0), 10);
  };

  const renderNotificationCard = () => {
    const unreadCount = getUnreadNotificationCount();
    const latestNotifications = [...notifications.filter(isUnreadNotification)]
      .sort(
        (a, b) =>
          new Date(b.date as string | number).valueOf() -
          new Date(a.date as string | number).valueOf()
      )
      .slice(0, 5);

    dayjs.locale(i18n.resolvedLanguage || "fr");
    if (
      String(userData.display_notification) === "0" ||
      String(userData.allow_notification) === "0"
    ) {
      return null;
    }

    return (
      <aside className="DashboardLayout-side">
        <div className="DashboardFeatureCard DashboardFeatureCard--notifications">
          <div className="DashboardFeatureCard-header">
            <span className="DashboardFeatureCard-kicker">
              {t("dashboard.notifications_kicker")}
            </span>
            <span className="DashboardFeatureCard-badge">
              {t("dashboard.notifications_badge", { count: unreadCount })}
            </span>
          </div>
          <div className="DashboardFeatureCard-feed">
            {!notificationsLoaded ? (
              <p className="DashboardFeatureCard-empty">{t("dashboard.notifications_loading")}</p>
            ) : latestNotifications.length === 0 ? (
              <p className="DashboardFeatureCard-empty">{t("dashboard.notifications_empty")}</p>
            ) : (
              <ul className="DashboardFeatureCard-feedList">
                {latestNotifications.map((notification) => {
                  const notificationPreview = String(notification.content || "")
                    .replace(/<[^>]*>/g, " ")
                    .replace(/\s+/g, " ")
                    .trim()
                    .slice(0, 120);

                  return (
                    <li className="DashboardFeatureCard-item" key={notification.id}>
                      <strong className="DashboardFeatureCard-itemTitle">
                        {notification.title as React.ReactNode}
                      </strong>
                      {notificationPreview ? (
                        <p className="DashboardFeatureCard-itemPreview">
                          {notificationPreview}
                          {notificationPreview.length >= 120 ? "…" : ""}
                        </p>
                      ) : null}
                      <span className="DashboardFeatureCard-itemDate">
                        {dayjs(notification.date as string | number).fromNow()}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="DashboardFeatureCard-actions">
            <Link to={BASE_PATH + "/notification"} className="Button DashboardFeatureCard-button">
              {t("dashboard.notifications_button")}
            </Link>
          </div>
        </div>
      </aside>
    );
  };

  const dashboardCards = (
    [
      {
        key: "roadmap",
        className: "DashboardQuickCard--roadmap",
        icon: roadmapIcon,
        library: getDocumentLibrary(modules, "marketing_roadmap"),
      },
      {
        key: "tournees",
        className: "DashboardQuickCard--tournees",
        icon: tourneesIcon,
        library: getDocumentLibrary(modules, "marketing_tournees"),
      },
      {
        key: "seminaire_international",
        className: "DashboardQuickCard--seminaireInternational",
        icon: seminaireInternationalIcon,
        library: getDocumentLibrary(modules, "event_seminaire_international"),
      },
      {
        key: "myformulation",
        className: "DashboardQuickCard--myformulation",
        icon: myformulationIcon,
        library: getDocumentLibrary(modules, "reglementaires_myformulation"),
      },
    ] as Array<{
      key: string;
      className: string;
      icon: string;
      library: { routePath?: string; frontPermission?: string; externalUrl?: string | null } | null;
    }>
  ).filter((card) => card.library && isAllowed(card.library.frontPermission));

  const customer = customerData as UserCustomer & {
    company_name?: string;
  };

  return (
    <div className="DashboardLanding">
      <div className="DashboardLanding-content">
        <div className="DashboardLayout">
          <div className="DashboardLayout-main">
            <section className="DashboardHeaderBanner">
              <div className="DashboardHeaderBanner-copy">
                <p className="DashboardHeaderBanner-kicker">{t("dashboard.hero_kicker")}</p>
                <h1 className="DashboardHeaderBanner-title">{t("dashboard.hero_title")}</h1>
                <p className="DashboardHeaderBanner-text">{t("dashboard.hero_text")}</p>
              </div>
              <div className="DashboardHeaderBanner-metrics">
                <div className="DashboardMetricCard">
                  <span className="DashboardMetricCard-label">{t("dashboard.metric_company")}</span>
                  <strong className="DashboardMetricCard-value">{customer.company_name}</strong>
                </div>
                <div className="DashboardMetricCard">
                  <span className="DashboardMetricCard-label">{t("dashboard.metric_account")}</span>
                  <strong className="DashboardMetricCard-value">{customer.id}</strong>
                </div>
                <div className="DashboardMetricCard">
                  <span className="DashboardMetricCard-label">
                    {t("dashboard.metric_notifications")}
                  </span>
                  <strong className="DashboardMetricCard-value">
                    {getUnreadNotificationCount()}
                  </strong>
                </div>
              </div>
            </section>

            <section className="DashboardHighlights">
              <div className="DashboardHighlights-main">
                <ul className="DashboardQuickLinks">
                  {dashboardCards.map((card) => {
                    const cardContent = (
                      <>
                        <span className="DashboardQuickCard-icon" aria-hidden="true">
                          <img
                            className="DashboardQuickCard-iconImage"
                            src={card.icon}
                            alt=""
                            width={64}
                            height={64}
                          />
                        </span>
                        <strong className="DashboardQuickCard-title">
                          {t(`dashboard.cards.${card.key}.title`)}
                        </strong>
                        <span className="DashboardQuickCard-text">
                          {t(`dashboard.cards.${card.key}.text`)}
                        </span>
                      </>
                    );

                    return (
                      <li className="DashboardQuickLinks-item" key={card.key}>
                        {card.library!.externalUrl ? (
                          <a
                            href={card.library!.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={"DashboardQuickCard " + card.className}
                          >
                            {cardContent}
                          </a>
                        ) : (
                          <Link
                            to={card.library!.routePath || "#"}
                            className={"DashboardQuickCard " + card.className}
                          >
                            {cardContent}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          </div>

          {renderNotificationCard()}
        </div>
      </div>
    </div>
  );
}

function withNotificationData(
  Component: React.ComponentType<DashboardPageProps>
): React.ComponentType<
  DashboardPageOwnProps &
    Omit<DashboardPageStateProps, "notifications" | "notificationsLoaded"> &
    ModulesQueryProps &
    AuthInjectedProps
> {
  return function DashboardNotificationData(props) {
    const customer = props.customerData as UserCustomer;
    const notificationsQuery = useNotifications(customer?.id);

    return (
      <Component
        {...props}
        notifications={notificationsQuery.notifications}
        notificationsLoaded={notificationsQuery.notificationsLoaded}
      />
    );
  };
}

export default withTranslation()(
  withAuthData(withModulesData(withNotificationData(DashboardPage)))
);
